import React, { useState } from 'react';
import { Box, Card, Typography, Button } from '@mui/material';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import MultiStepFrom from './MultiStepFrom';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2 } from 'src/utils/colors';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const colorScheme = {
  primary: COLOR_PRIMARY_1,
  primaryDark: COLOR_PRIMARY_2,
};

interface RealtorFormProps {
  location: string;
  /** When provided, modal open state is controlled by parent (use with onClose and onOpen). */
  open?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  /** When true, only render the modal (no card). Use when modal is opened from another CTA. */
  hideCard?: boolean;
}

const RealtorForm: React.FC<RealtorFormProps> = ({
  location,
  open: controlledOpen,
  onClose,
  onOpen,
  hideCard = false,
}) => {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));

  const isControlled = controlledOpen !== undefined;
  const isModalOpen = isControlled ? controlledOpen : internalOpen;
  const handleOpen = isControlled ? (onOpen ?? (() => {})) : () => setInternalOpen(true);
  const handleClose = isControlled ? (onClose ?? (() => {})) : () => setInternalOpen(false);

  const handleConnectClick = () => {
    handleOpen();
  };

  return (
    <>
      {isModalOpen && (
        <MultiStepFrom
          location={location}
          setIsOpen={(open: boolean) => {
            if (!open) handleClose();
          }}
        />
      )}
      {!hideCard && (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            width: '100%',
            borderRadius: 3,
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            padding: { xs: 3, sm: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '24px', sm: '28px', md: '32px' },
                lineHeight: 1.2,
                textAlign: 'left',
              }}
            >
              <span style={{ color: colorScheme.primaryDark }}>Connect with Agents</span>{' '}
              specializing in Short-Term Rentals in {location}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '14px', sm: '16px' },
                lineHeight: 1.5,
                textAlign: 'left',
              }}
            >
              Who can help you find investment properties with strong revenue potential.
            </Typography>
          </Box>

          <Button
            variant="contained"
            sx={{
              backgroundColor: colorScheme.primary,
              width: matches ? '100%' : '250px',
              color: '#FFFFFF',
              borderRadius: 2,
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: colorScheme.primaryDark,
                transform: 'translateY(-2px)',
              },
              boxShadow: '0px 2px 8px rgba(74, 20, 140, 0.3)',
            }}
            onClick={handleConnectClick}
          >
            Connect to an Agent
            <ArrowRightAltRoundedIcon sx={{ fontSize: { xs: '20px', sm: '28px' } }} />
          </Button>
        </Card>
      </Box>
      )}
    </>
  );
};

export default RealtorForm;
